import { Box, Button, ListEntry, OSKIcon, OSKThemeType, Spinner, Typography } from 'oskcomponents';
import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { OSKContentView } from '~/molecules/OSKContentView';

import { DataResourceProvider } from '~/redux';
import { date_format_long, date_format_longer, getProgramId, utc_date_format } from '~/utils';
import { RootState } from '~/redux/store';
import { connect } from 'react-redux';
import { noop, OrdersAPI, OSKGeoJson, OSKScene, StacAPI } from 'oskcore';

import ExternalOrderDashboardTable, { TempOrderType } from './table';
import ExternalOrderDashboardPaginator from './paginator';
import { TaskingOrder } from 'oskcore/src/api/orders';
import { StacItem } from 'stac-ts';
import EditableMap from '~/organisms/map/EditableMap';
import { CollapsableGroup, MapCentroid } from '~/atoms';
import { LatLng } from 'leaflet';
import SceneFootprints from '~/organisms/map/SceneFootprints';
import { SHORT_VISIBLE_PAGE_SIZE } from '~/pagination';

const ORDER_TABLE_PAGE_SIZE = SHORT_VISIBLE_PAGE_SIZE;

const ExternalOrderDashboardView = () => {
    const theme = useTheme() as OSKThemeType;

    const [taskingOrders, setTaskingOrders] = useState<TaskingOrder[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingStac, setIsLoadingStac] = useState<boolean>(false);
    const [totalOrders, setTotalOrders] = useState<number | undefined>(0);
    const [currentPage, setCurrentPage] = useState<number | undefined>();
    const [selectedOrder, setSelectedOrder] = useState<TempOrderType | undefined>();
    const [selectedStacItems, setSelectedStacItems] = useState<StacItem[]>([]);

    useEffect(() => {
        setIsLoadingStac(true);
        StacAPI.searchCatalog({
            taskingOrderIds: [selectedOrder?.id ?? ''],
        })
            .then((result) => setSelectedStacItems(result.features as StacItem[]))
            .finally(() => setIsLoadingStac(false));
    }, [selectedOrder]);

    useEffect(() => {
        setIsLoading(true);
        /* Warning: Passing in an explicit value for 'next' is not how the API
        was designed and we assume all responsibility for calculating the item index
        ourselves instead of loading the 'next' href in `links`. */
        OrdersAPI.getOrders({
            limit: ORDER_TABLE_PAGE_SIZE,
            next: (currentPage ?? 0) * ORDER_TABLE_PAGE_SIZE,
        })
            .then((response) => {
                if (response.features?.length > 0) {
                    setTotalOrders(response.numberMatched);
                    setTaskingOrders(response.features);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage]);

    const SelectedAOI: OSKGeoJson | undefined = useMemo(() => {
        if (!selectedOrder) return undefined;

        const { coordinates } = selectedOrder?.geometry;
        return OSKGeoJson.fromCoordinate({ lat: coordinates[0], lng: coordinates[1] } as LatLng);
    }, [selectedOrder]);

    return (
        <OSKContentView nogradient>
            <DataResourceProvider features={['order_multiple']} programId={getProgramId()} orderPage={0}>
                <Box grow>
                    <Box
                        style={{ width: '100%', height: '100%', padding: '14px' }}
                        bg={theme.colors.white}
                        fg={theme.colors.black}
                        grow
                        col
                    >
                        <Box
                            style={{
                                justifyContent: 'space-between',
                                marginBottom: '12px',
                            }}
                        >
                            <Typography variant="heading2">Order Dashboard</Typography>
                            <Button variant="contrast">
                                <Box>
                                    <OSKIcon code="refresh" fill={theme.colors.black900} scale={120} />
                                    <Typography
                                        style={{ paddingLeft: '4px' }}
                                        variant="body2"
                                        color={theme.colors.black900}
                                    >
                                        Refresh
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                        {isLoading ? (
                            <Box grow center="all">
                                <Spinner variant="Box" size="Large" />
                            </Box>
                        ) : (
                            <Box col style={{ marginRight: '8px' }}>
                                <ExternalOrderDashboardTable
                                    orders={taskingOrders}
                                    onRowClick={(row: TempOrderType) => {
                                        setSelectedOrder(row);
                                    }}
                                />
                                <ExternalOrderDashboardPaginator
                                    totalOrders={totalOrders}
                                    currentPage={currentPage}
                                    pageSize={ORDER_TABLE_PAGE_SIZE}
                                    changePage={(pageNumber: number) => {
                                        setCurrentPage(pageNumber);
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Box
                        style={{
                            backgroundColor: theme.colors.whiteAccentBackground,
                            borderLeft: `1px solid ${theme.colors.black200}`,
                            padding: '14px',
                            color: theme.colors.black,
                            minWidth: '450px',
                            maxWidth: '450px',
                            overflowY: 'scroll',
                        }}
                        grow
                        col
                    >
                        {selectedOrder ? (
                            <>
                                <Typography variant="heading3">
                                    {selectedOrder.properties.order_parameters.name}
                                </Typography>

                                <Box
                                    style={{ minHeight: '200px', border: `1px solid ${theme.colors.black200}` }}
                                    mt={20}
                                >
                                    <EditableMap useOSKLowbar={false}>
                                        <SceneFootprints
                                            scenes={[
                                                {
                                                    geometry: {
                                                        coordinates: SelectedAOI?.features[0].coordinates,
                                                        type: 'Point',
                                                    },
                                                } as unknown as OSKScene,
                                            ]}
                                        />
                                        <MapCentroid area={SelectedAOI} />
                                    </EditableMap>
                                </Box>
                                <OrderDetail label="Order ID" value={selectedOrder.id} strong />
                                <OrderDetail
                                    label="Created Date"
                                    value={date_format_longer(selectedOrder.properties.created)}
                                    strong
                                />
                                <OrderDetail
                                    label="Point of Contact"
                                    value={selectedOrder.properties.order_parameters.point_of_contact}
                                    strong
                                />

                                <Box style={{ marginTop: '14px' }} col>
                                    <Typography variant="heading4" color={theme.colors.black500}>
                                        Scenes
                                    </Typography>

                                    {isLoadingStac ? (
                                        <Box center="all" mt={50}>
                                            <Spinner size="Medium" variant="Box" />
                                        </Box>
                                    ) : (
                                        <>
                                            {selectedStacItems.length > 0 ? (
                                                <>
                                                    <OrderDetail
                                                        label="Platform"
                                                        value={selectedStacItems[0].properties.platform}
                                                        strong
                                                    />
                                                </>
                                            ) : (
                                                <Box mt={4}>
                                                    <Typography variant="body2">
                                                        There are currently no scenes delivered for this order.
                                                    </Typography>
                                                </Box>
                                            )}
                                            {selectedStacItems.map((item) => (
                                                <CollapsableGroup
                                                    key={`item-assets-${item.id}`}
                                                    style={{
                                                        backgroundColor: 'white',
                                                        border: `1px solid ${theme.colors.lightGray}`,
                                                        borderRadius: '5px',
                                                        padding: '8px 12px',
                                                        marginTop: '20px',
                                                        width: '100%',
                                                    }}
                                                    title={
                                                        <Box
                                                            style={{
                                                                justifyContent: 'space-between',
                                                                paddingRight: '24px',
                                                            }}
                                                            center="all"
                                                            grow
                                                        >
                                                            <Typography variant="heading5">
                                                                {utc_date_format(item.properties.datetime)}
                                                            </Typography>
                                                            <Box col>
                                                                <Typography
                                                                    variant="body3"
                                                                    strong
                                                                    style={{ userSelect: 'none' }}
                                                                >
                                                                    Last Updated
                                                                </Typography>
                                                                <Typography variant="body3">
                                                                    {date_format_long(item.properties.updated)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                >
                                                    <Box
                                                        style={{
                                                            backgroundColor: 'white',
                                                            padding: '14px',
                                                            borderRadius: '5px',
                                                            marginTop: '8px',
                                                        }}
                                                        col
                                                        grow
                                                    >
                                                        {Object.values(item.assets).map((asset, idx) => (
                                                            <OrderDownload
                                                                key={`download-${idx}`}
                                                                label={asset.title}
                                                                subtitle={asset.type}
                                                                uri={asset.href}
                                                            />
                                                        ))}
                                                    </Box>
                                                </CollapsableGroup>
                                            ))}
                                        </>
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box col grow center="all">
                                <OSKIcon code="no-orders" width={250} />
                                <Typography variant="body2">No order selected.</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DataResourceProvider>
        </OSKContentView>
    );
};

type OrderDetailProps = {
    strong?: boolean;
    noBorder?: boolean;
    subtitle?: string;
} & ListEntry;
const OrderDetail = ({ label, subtitle, value, strong, noBorder }: OrderDetailProps) => {
    const theme = useTheme() as OSKThemeType;

    return (
        <Box
            style={{
                borderBottom: noBorder ? 'none' : `1px solid ${theme.colors.black200}`,
                padding: '14px 0px',
                width: '100%',
                justifyContent: 'space-between',
            }}
            center="vertical"
        >
            <Box col>
                <Typography variant="body3" strong={strong} style={{ minWidth: '150px', marginRight: '8px' }}>
                    {label}
                </Typography>
                <Typography variant="caption2" strong={strong} style={{ minWidth: '150px', marginRight: '8px' }}>
                    {subtitle}
                </Typography>
            </Box>
            <Typography variant="body3">{value}</Typography>
        </Box>
    );
};

type OrderDownloadProps = {
    label?: string;
    subtitle?: string;
    uri?: string;
};
const OrderDownload = ({ label, subtitle, uri }: OrderDownloadProps) => {
    const theme = useTheme() as OSKThemeType;

    return (
        <Box
            style={{
                padding: '12px 14px',
                width: '100%',
                justifyContent: 'space-between',
                borderRadius: '5px',
                border: `1px solid ${theme.colors.black200}`,
                margin: '8px 0px',
            }}
            hoverBg={theme.colors.black200}
            center="vertical"
            onClick={() => {
                window.open(uri);
            }}
        >
            <Box col>
                <Typography variant="body3" strong style={{ minWidth: '150px', maxWidth: '275px', marginRight: '8px' }}>
                    {label}
                </Typography>
                <Typography variant="body3" style={{ minWidth: '150px', marginRight: '8px' }}>
                    {subtitle}
                </Typography>
            </Box>
            {uri && (
                <OSKIcon code="download-modern" fill={theme.colors.black600} hoverFill={theme.colors.black200} p={12} />
            )}
        </Box>
    );
};

const mapStateToProps = (state: RootState) => {
    return {};
};

export default connect(mapStateToProps, noop)(ExternalOrderDashboardView);
